import React from 'react';
import Pill from '@/components/ui/Pill';
import Text from '@/components/ui/Text';
import cn from '@/utils/cn';
import type { DerivedStates, LaneState } from './deriveParticipantStates';
import StateBar, { LANE_LABEL } from './StateBar';
import { absoluteLabel } from './timeFormat';
import type { EntryInfo, ParticipantStudent } from './types';

interface StudentLanesProps {
  derived: DerivedStates;
  selectedStudentId: number | null;
  students: ParticipantStudent[];
  onSelect: (studentId: number | null) => void;
}

const LEGEND: LaneState[] = ['following', 'disconnected', 'free', 'off_page'];
const LEGEND_CLASS: Record<LaneState, string> = {
  disconnected: 'tw-bg-red-500',
  following: 'tw-bg-green-500',
  free: 'tw-bg-yellow-400',
  off_page: 'tw-bg-gray-200',
};

const SECTION_CLASS = cn(
  'tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-p-4',
  'tw-flex tw-flex-col tw-gap-3',
);

const ROW_BUTTON_CLASS = cn(
  'tw-w-full tw-bg-transparent tw-border-0 tw-p-0 tw-text-left tw-cursor-pointer',
  'tw-flex tw-flex-col tw-gap-1.5',
);

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="tw-grid tw-grid-cols-[110px_1fr] tw-gap-2 tw-text-xs">
    <span className="tw-text-very-dark-grey">{label}</span>
    <span className="tw-text-tci-black tw-break-all">{value ?? '—'}</span>
  </div>
);

const Flag = ({ children, pillColor, solid = false }: { children: string; pillColor: string; solid?: boolean }) => (
  <Pill outline={!solid} pillColor={pillColor} solid={solid}>
    <Text size="xs" tag="span" weight={solid ? 'bold' : undefined}>{children}</Text>
  </Pill>
);

const EntryDetails = ({ entry }: { entry: EntryInfo | null }) => {
  if (!entry) return <Field label="Entry" value="No page load recorded" />;
  const when = absoluteLabel(entry.occurred_at);
  return (
    <>
      <Field label="Entered" value={`${entry.entry_mode ?? '?'} (${entry.nav_type ?? '?'}) at ${when}`} />
      <Field label="Hydrated" value={entry.hydrated_session_id ? 'yes, locked on load' : 'no, free on load'} />
      <Field label="URL" value={entry.url} />
      <Field label="Referrer" value={entry.referrer} />
      <Field label="User agent" value={entry.user_agent} />
    </>
  );
};

const ParticipationDetails = ({ student }: { student: ParticipantStudent }) => {
  const { participation } = student;
  if (!participation) return <Field label="Presence" value="Never marked present" />;
  return (
    <>
      <Field label="First joined" value={absoluteLabel(participation.first_joined_at)} />
      <Field
        label="Last left"
        value={participation.last_left_at ? absoluteLabel(participation.last_left_at) : 'still present at end'}
      />
      <Field label="Rejoins" value={participation.rejoin_count} />
      {participation.removed_at && <Field label="Removed" value={absoluteLabel(participation.removed_at)} />}
    </>
  );
};

const StudentDetails = ({ student }: { student: ParticipantStudent }) => {
  const { counts, entry } = student;
  return (
    <div className="tw-flex tw-flex-col tw-gap-1.5 tw-pt-2 tw-pl-1 tw-border-t tw-border-solid tw-border-gray-100">
      <EntryDetails entry={entry.first_page_entered} />
      {entry.page_load_count > 1 && (
        <Field label="Page loads" value={`${entry.page_load_count} (more than one tab or reload)`} />
      )}
      {entry.hydration_results.length > 0 && (
        <Field
          label="Hydrations"
          value={entry.hydration_results.map(h => `${h.result} (${h.reason})`).join(', ')}
        />
      )}
      <ParticipationDetails student={student} />
      <Field
        label="Counts"
        value={[
          `${counts.nav_applied} slides shown`,
          `${counts.ws_disconnected} socket drops`,
          `${counts.channel_rejected} rejections`,
        ].join(' · ')}
      />
    </div>
  );
};

const StudentLanes = ({ derived, selectedStudentId, students, onSelect }: StudentLanesProps) => (
  <section aria-label="Students" className={SECTION_CLASS}>
    <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
      <Text className="tw-text-tci-black tw-m-0" size="sm" tag="h2" variant="label">
        Students ({students.length})
      </Text>
      <ul className="tw-list-none tw-m-0 tw-p-0 tw-flex tw-gap-3 tw-flex-wrap">
        {LEGEND.map(state => (
          <li key={state} className="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-very-dark-grey">
            <span aria-hidden className={cn('tw-h-2 tw-w-3 tw-rounded-sm', LEGEND_CLASS[state])} />
            {LANE_LABEL[state]}
          </li>
        ))}
      </ul>
    </div>

    {students.length === 0 && (
      <Text className="tw-text-very-dark-grey tw-m-0" size="sm">No students were invited or seen.</Text>
    )}

    <ul className="tw-list-none tw-m-0 tw-p-0 tw-flex tw-flex-col tw-gap-2">
      {students.map((student) => {
        const state = derived.students[student.id];
        const selected = selectedStudentId === student.id;
        return (
          <li
            key={student.id}
            className={cn(
              'tw-rounded tw-p-2 tw-border tw-border-solid',
              selected ? 'tw-border-tci-purple tw-bg-tci-purple/5' : 'tw-border-transparent',
            )}
          >
            <button
              aria-expanded={selected}
              className={ROW_BUTTON_CLASS}
              type="button"
              onClick={() => onSelect(selected ? null : student.id)}
            >
              <span className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                <Text className="tw-text-tci-black tw-m-0" size="sm" tag="span" weight="bold">
                  {student.full_name}
                </Text>
                {!student.invited && <Flag pillColor="grey">not invited</Flag>}
                {student.participation?.removed_at && <Flag pillColor="red">removed</Flag>}
                {state?.flags.stuckSuspect && <Flag solid pillColor="red">stuck?</Flag>}
                {state?.flags.freeSuspect && <Flag solid pillColor="yellow">free?</Flag>}
              </span>
              <StateBar
                end={derived.end}
                intervals={state?.intervals ?? []}
                label={student.full_name}
                start={derived.start}
                teacherDrops={derived.teacherDrops}
              />
            </button>
            {selected && <StudentDetails student={student} />}
          </li>
        );
      })}
    </ul>
  </section>
);

export default StudentLanes;
